import { useState } from 'react';
import type { Translations } from '../types';

interface AdminDrawerProps {
  t: Translations;
  onUpdate: (key: string, value: string) => void;
  onSave: () => void;
  onLogout: () => void;
}

export default function AdminDrawer({ t, onUpdate, onLogout }: AdminDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'seo' | 'company' | 'legal'>('seo');

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg('Przetwarzanie...');
    
    const token = localStorage.getItem('jwt_token');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPasswordMsg('Hasło zmienione pomyślnie!');
        setTimeout(() => {
          setIsPasswordModalOpen(false);
          setOldPassword('');
          setNewPassword('');
          setPasswordMsg('');
        }, 2000);
      } else {
        setPasswordMsg(data.detail || 'Wystąpił błąd');
      }
    } catch (error) {
      setPasswordMsg('Błąd połączenia z serwerem');
    }
  };

  return (
    <>
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-[100] w-12 h-12 bg-white text-[#125b60] rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform cursor-pointer border border-slate-100"
      >
        <i className="ri-settings-4-line text-2xl"></i>
      </button>

      <div 
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[110] transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsOpen(false)}
      ></div>

      <div className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[120] transform transition-transform duration-300 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        
        <div className="flex flex-col p-6 border-b border-slate-100 bg-[#FAF8F5]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-extrabold text-[#125b60] text-lg">Ustawienia</h3>
            <button onClick={() => setIsOpen(false)} className="w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-sm text-slate-400 hover:text-[#125b60] cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setIsPasswordModalOpen(true)} className="text-xs text-[#c69c6d] hover:text-[#b58b5c] font-bold transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-lock-password-line"></i> Zmień hasło
            </button>
            <button onClick={onLogout} className="text-xs text-slate-500 hover:text-red-500 font-bold transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-logout-box-r-line"></i> Wyloguj
            </button>
          </div>
        </div>

        <div className="flex p-2 bg-slate-50 border-b border-slate-100 gap-2">
          <button onClick={() => setActiveTab('seo')} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors cursor-pointer ${activeTab === 'seo' ? 'bg-white shadow-sm text-[#125b60]' : 'text-slate-500 hover:bg-slate-100'}`}>
            SEO
          </button>
          <button onClick={() => setActiveTab('company')} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors cursor-pointer ${activeTab === 'company' ? 'bg-white shadow-sm text-[#125b60]' : 'text-slate-500 hover:bg-slate-100'}`}>
            Firma
          </button>
          <button onClick={() => setActiveTab('legal')} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors cursor-pointer ${activeTab === 'legal' ? 'bg-white shadow-sm text-[#125b60]' : 'text-slate-500 hover:bg-slate-100'}`}>
            Prawne
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {activeTab === 'seo' && (
            <>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Tytuł strony (Zakładka)</label>
                <input value={t.seo_title || ''} onChange={(e) => onUpdate('seo_title', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Opis dla Google (Meta Description)</label>
                <textarea rows={3} value={t.seo_desc || ''} onChange={(e) => onUpdate('seo_desc', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm resize-none" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Słowa kluczowe</label>
                <input value={t.seo_keywords || ''} onChange={(e) => onUpdate('seo_keywords', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" />
              </div>
            </>
          )}

          {activeTab === 'company' && (
            <>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">E-mail (odbiór formularzy)</label>
                <input value={t.company_email || ''} onChange={(e) => onUpdate('company_email', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Telefon kontaktowy</label>
                <input value={t.company_phone || ''} onChange={(e) => onUpdate('company_phone', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Nazwa Firmy</label>
                <input value={t.company_name || ''} onChange={(e) => onUpdate('company_name', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Kod pocztowy</label>
                  <input value={t.company_zip || ''} onChange={(e) => onUpdate('company_zip', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Miasto</label>
                  <input value={t.company_city || ''} onChange={(e) => onUpdate('company_city', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" />
                </div>
              </div>
            </>
          )}

          {activeTab === 'legal' && (
            <>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Treść: Impressum</label>
                <textarea rows={8} value={t.impressum_content || ''} onChange={(e) => onUpdate('impressum_content', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm resize-none custom-scrollbar" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-2">Treść: Polityka Prywatności</label>
                <textarea rows={8} value={t.datenschutz_content || ''} onChange={(e) => onUpdate('datenschutz_content', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm resize-none custom-scrollbar" />
              </div>
            </>
          )}
        </div>
      </div>

      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full relative">
            <button 
              onClick={() => {
                setIsPasswordModalOpen(false);
                setPasswordMsg('');
                setOldPassword('');
                setNewPassword('');
              }} 
              className="absolute top-4 right-4 text-2xl text-slate-400 hover:text-[#125b60] transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
            <h2 className="text-xl font-extrabold text-[#125b60] mb-6 flex items-center gap-2">
              <i className="ri-lock-password-line text-[#c69c6d]"></i> Zmiana hasła
            </h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Stare hasło</label>
                <input 
                  type="password" 
                  value={oldPassword} 
                  onChange={(e) => setOldPassword(e.target.value)} 
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" 
                  required 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Nowe hasło</label>
                <input 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#c69c6d] text-sm" 
                  required 
                  minLength={8}
                />
              </div>
              {passwordMsg && (
                <p className={`text-sm text-center font-bold mt-2 ${passwordMsg.includes('pomyślnie') ? 'text-green-600' : 'text-red-500'}`}>
                  {passwordMsg}
                </p>
              )}
              <button type="submit" className="w-full py-3 bg-[#c69c6d] text-white font-bold rounded-xl hover:bg-[#b58b5c] transition-colors cursor-pointer mt-4">
                Zatwierdź
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}